import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  InputAdornment,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  OpenInNew as OpenInNewIcon,
  NotificationsActive as NotificationsActiveIcon,
  AddAlarm as AddAlarmIcon,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import openScheduleService from '../services/openScheduleService';
import { getPropertyViewMode, canManageBothSalesAndRentals } from '../utils/roleUtils';
import attendeeTagService from '../services/attendeeTagService';
import attendeeFollowUpReminderService, {
  type AttendeeFollowUpReminder,
} from '../services/attendeeFollowUpReminderService';
import AttendeeFollowUpReminderDialog from '../components/AttendeeFollowUpReminderDialog';

interface Attendee {
  name: string;
  email?: string;
  phone: string;
  registeredAt: string;
  firstVisit: string;
  property: {
    _id?: string;
    address?: any;
    suburb?: string;
    state?: string;
    postcode?: string;
  };
  visits: number;
  properties: string[];
  tags?: string[];
}

const Attendees = () => {
  const { agency, user } = useAuth();
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasAttendeeAccess = ['professional', 'agency premium', 'starter', 'premium', 'enterprise', 'demo', 'individual', 'elite individual'].includes(planName);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [stats, setStats] = useState({ totalAttendees: 0, totalVisits: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selected, setSelected] = useState<Attendee | null>(null);
  const [searching, setSearching] = useState(false);
  const [followUpReminders, setFollowUpReminders] = useState<Record<string, AttendeeFollowUpReminder>>({});
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderAttendee, setReminderAttendee] = useState<Attendee | null>(null);
  const isInitialLoadRef = useRef(true);

  const viewMode = getPropertyViewMode(user);
  const showSaleRentalToggle = canManageBothSalesAndRentals(user);
  const [saleTypeGroup, setSaleTypeGroup] = useState<'sale' | 'rental'>(
    viewMode === 'rental' ? 'rental' : 'sale'
  );

  const fetchAttendees = async () => {
    if (!agency?.slug) return;
    
    try {
      const isInitialLoad = isInitialLoadRef.current;
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearching(true);
      }
      const data = await openScheduleService.getAllAttendees(agency.slug, { 
        search: searchTerm,
        tags: selectedTags,
        saleTypeGroup,
      });
      setAttendees(data.attendees);
      setStats({ totalAttendees: data.totalAttendees, totalVisits: data.totalVisits });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      if (isInitialLoadRef.current) {
        setLoading(false);
        isInitialLoadRef.current = false;
      } else {
        setSearching(false);
      }
    }
  };

  useEffect(() => {
    if (agency?.slug) {
      fetchAttendees();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agency?.slug, searchTerm, selectedTags, saleTypeGroup]);

  useEffect(() => {
    if (!agency?.slug) return;
    let isMounted = true;

    const loadReminders = async () => {
      try {
        const data = await attendeeFollowUpReminderService.getAttendeeFollowUpReminders(
          agency.slug!,
          { isActive: true }
        );
        if (!isMounted) return;
        const byPhone: Record<string, AttendeeFollowUpReminder> = {};
        data.forEach((reminder) => {
          byPhone[reminder.attendeePhone.toLowerCase()] = reminder;
        });
        setFollowUpReminders(byPhone);
      } catch (err) {
        console.error('Failed to load follow-up reminders', err);
      }
    };

    loadReminders();

    return () => {
      isMounted = false;
    };
  }, [agency?.slug]);

  const reloadReminders = async () => {
    if (!agency?.slug) return;
    try {
      const data = await attendeeFollowUpReminderService.getAttendeeFollowUpReminders(
        agency.slug,
        { isActive: true }
      );
      const byPhone: Record<string, AttendeeFollowUpReminder> = {};
      data.forEach((reminder) => {
        byPhone[reminder.attendeePhone.toLowerCase()] = reminder;
      });
      setFollowUpReminders(byPhone);
    } catch (err) {
      console.error('Failed to reload follow-up reminders', err);
    }
  };

  const formatReminderTooltip = (reminder: AttendeeFollowUpReminder) => {
    const when = formatDate(reminder.reminderTime);
    const repeat = reminder.isRecurring
      ? ` · repeats ${reminder.recurringPattern?.type || 'regularly'}`
      : '';
    return `Follow-up: ${when}${repeat}`;
  };

  const openReminderDialog = (attendee: Attendee) => {
    setReminderAttendee(attendee);
    setReminderDialogOpen(true);
  };

  useEffect(() => {
    if (!agency?.slug) return;
    let isMounted = true;

    const loadTags = async () => {
      try {
        setTagsLoading(true);
        const data = await attendeeTagService.getAttendeeTags(agency.slug!);
        if (!isMounted) return;
        const combined = Array.from(new Set<string>(data.allTags));
        setTagOptions(combined);
      } catch (err) {
        console.error('Failed to load attendee tags', err);
      } finally {
        if (isMounted) {
          setTagsLoading(false);
        }
      }
    };

    loadTags();

    return () => {
      isMounted = false;
    };
  }, [agency?.slug]);

  const formatPropertyAddress = (property: Attendee['property']): string => {
    if (!property) return 'Unknown';
    if (typeof property.address === 'string') return property.address;
    if (property.address?.street) {
      const parts = [
        property.address.street,
        property.suburb,
        property.state,
        property.postcode
      ].filter(Boolean);
      return parts.join(', ');
    }
    const parts = [property.suburb, property.state, property.postcode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Unknown';
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const getInitials = (name: string) => {
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!hasAttendeeAccess) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Upgrade Required
            </Typography>
            <Typography color="text.secondary">
              Attendee registration and management are available starting from the Starter plan. Upgrade your subscription to unlock attendee tracking, follow-ups, and contract workflows.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Attendees
          </Typography>
          {showSaleRentalToggle && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={saleTypeGroup === 'sale' ? 'contained' : 'outlined'}
                onClick={() => setSaleTypeGroup('sale')}
              >
                Sales
              </Button>
              <Button
                size="small"
                variant={saleTypeGroup === 'rental' ? 'contained' : 'outlined'}
                onClick={() => setSaleTypeGroup('rental')}
              >
                Rentals
              </Button>
            </Box>
          )}
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={6}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Attendees
                </Typography>
                <Typography variant="h4">{stats.totalAttendees}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Visits
                </Typography>
                <Typography variant="h4" color="primary">{stats.totalVisits}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Search */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search attendees by name, email, phone, or property..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Box mt={2}>
            <Autocomplete
              multiple
              options={tagOptions}
              value={selectedTags}
              loading={tagsLoading}
              onChange={(event, newValue) => {
                const uniqueValues = Array.from(new Set(newValue));
                setSelectedTags(uniqueValues);
              }}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by labels"
                  placeholder="Select labels"
                />
              )}
            />
          </Box>
        </Paper>

        {/* Attendees Table */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Attendees ({attendees.length})
          </Typography>

          {attendees.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Attendee</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Property Visited</TableCell>
                    <TableCell>First Visit</TableCell>
                    <TableCell>Visits</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendees.map((attendee, index) => {
                    const initials = getInitials(attendee.name);
                    const rowKey = `${attendee.phone}-${index}`;
                    return (
                      <TableRow key={rowKey}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                              {initials}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {attendee.name}
                              </Typography>
                              {attendee.visits > 1 && (
                                <Chip
                                  label={`${attendee.visits} visits`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  sx={{ mt: 0.5 }}
                                />
                              )}
                              {attendee.tags && attendee.tags.length > 0 && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {attendee.tags.map((tag) => (
                                    <Chip key={`${rowKey}-${tag}`} label={tag} size="small" variant="outlined" />
                                  ))}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {attendee.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                                <Typography variant="body2">{attendee.email}</Typography>
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">{attendee.phone}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <LocationIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Box>
                              {!expandedRows[rowKey] ? (
                                <Typography variant="body2">
                                  {formatPropertyAddress(attendee.property)}
                                </Typography>
                              ) : (
                                <Box>
                                  {[formatPropertyAddress(attendee.property), ...attendee.properties.filter(Boolean)]
                                    .filter((v, i, arr) => v && arr.indexOf(v) === i)
                                    .map((addr, i) => (
                                      <Typography key={i} variant="body2">{addr}</Typography>
                                    ))}
                                </Box>
                              )}
                            </Box>
                          </Box>
                          {attendee.properties.length > 1 && !expandedRows[rowKey] && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setExpandedRows(prev => ({ ...prev, [rowKey]: true }))}
                              sx={{ mt: 0.5 }}
                            >
                              +{attendee.properties.length - 1} more
                            </Button>
                          )}
                          {expandedRows[rowKey] && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => setExpandedRows(prev => ({ ...prev, [rowKey]: false }))}
                              sx={{ mt: 0.5 }}
                            >
                              Show less
                            </Button>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {formatDate(attendee.firstVisit)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={attendee.visits} color="primary" variant="outlined" size="small" />
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const phoneKey = attendee.phone.toLowerCase();
                            const reminder = followUpReminders[phoneKey];
                            return (
                              <>
                                <Tooltip
                                  title={
                                    reminder
                                      ? formatReminderTooltip(reminder)
                                      : 'Set follow-up reminder'
                                  }
                                >
                                  <IconButton
                                    onClick={() => openReminderDialog(attendee)}
                                    color={reminder ? 'primary' : 'default'}
                                  >
                                    {reminder ? <NotificationsActiveIcon /> : <AddAlarmIcon />}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="View Details">
                                  <IconButton
                                    onClick={() => {
                                      setSelected(attendee);
                                      setDetailsOpen(true);
                                    }}
                                  >
                                    <OpenInNewIcon />
                                  </IconButton>
                                </Tooltip>
                              </>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary" variant="h6">
                No attendees found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {searchTerm
                  ? 'Try adjusting your search.'
                  : 'Attendees from open house viewings will appear here.'
                }
              </Typography>
            </Box>
          )}
        </Paper>

        {/* Attendee Details Dialog */}
        <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Attendee Details</DialogTitle>
          <DialogContent dividers>
            {selected && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="subtitle1">{selected.name}</Typography>
                {selected.email && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2">{selected.email}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{selected.phone}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  First Visit: {formatDate(selected.firstVisit)}
                </Typography>
                <Typography variant="body2">Visits: {selected.visits}</Typography>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Properties</Typography>
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {[formatPropertyAddress(selected.property), ...selected.properties.filter(Boolean)]
                      .filter((v, i, arr) => v && arr.indexOf(v) === i)
                      .map((addr, i) => (
                        <li key={i}>
                          <Typography variant="body2">{addr}</Typography>
                        </li>
                      ))}
                  </ul>
                </Box>
                {selected.tags && selected.tags.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Labels</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.tags.map(tag => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {reminderAttendee && agency?.slug && (
          <AttendeeFollowUpReminderDialog
            open={reminderDialogOpen}
            onClose={() => {
              setReminderDialogOpen(false);
              setReminderAttendee(null);
            }}
            agencySlug={agency.slug}
            attendeeName={reminderAttendee.name}
            attendeePhone={reminderAttendee.phone}
            attendeeEmail={reminderAttendee.email}
            propertyId={reminderAttendee.property?._id}
            existingReminder={followUpReminders[reminderAttendee.phone.toLowerCase()]}
            onSuccess={reloadReminders}
          />
        )}
      </Box>
    </Container>
  );
};

export default Attendees;

